import clsx from 'clsx';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleTooltip from '~/components/data-display/SimpleTooltip';
import Text, { TextProps } from '~/components/typographies/Text';
import IconArrowTrendDown from '~/icons/icon-arrow-trend-down.svg?react';
import IconArrowTrendUp from '~/icons/icon-arrow-trend-up.svg?react';
import { Document, DocumentStatus } from '~/types/document';
import { Money } from '~/types/money';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import { absMoney, formatMoney } from '~/utils/money';
import styles from './ReconciliationCommon.module.scss';

export const DocumentDescriptionText = ({ document }: { document: Document }) => {
  const { t } = useTranslation('reconciliations');

  const documentDescriptionKey =
    document.status === DocumentStatus.Draft ? 'document_description_draft' : 'document_description';

  return t(documentDescriptionKey, {
    type: t(`type.${document.documentType}`, { ns: 'documents' }),
    number: document.number ?? '',
    date: formatFromISOToDisplay(document.creationDate),
  });
};

export const EllipsedText = ({ children, ...props }: PropsWithChildren<TextProps>) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const parentElementWidth = tooltipRef.current?.offsetWidth;
    const tooltipElementWidth = tooltipRef.current?.scrollWidth;
    if (parentElementWidth && tooltipElementWidth) {
      setShowTooltip(tooltipElementWidth > parentElementWidth);
    }
  }, [children]);

  return (
    <Text {...props} className={clsx(styles.ellipsed, props.className)} element="div" ref={tooltipRef}>
      {showTooltip ? (
        <SimpleTooltip mode="span" title={children} delay={1000}>
          {children}
        </SimpleTooltip>
      ) : (
        children
      )}
    </Text>
  );
};

interface AmountWithIconProps {
  amount: Money;
  isInflow: boolean;
  iconSize: 'small' | 'medium';
  containerClassName?: string;
}

export const AmountWithIcon: React.FC<AmountWithIconProps & TextProps> = ({
  amount,
  isInflow,
  iconSize,
  containerClassName,
  ...props
}) => {
  const iconClass = clsx(styles.icon, {
    [styles.small!]: iconSize === 'small',
    [styles.medium!]: iconSize === 'medium',
    [styles.isInflow!]: isInflow,
  });

  const Icon = isInflow ? IconArrowTrendUp : IconArrowTrendDown;

  return (
    <div className={clsx(styles.amountWithIcon, containerClassName)}>
      <Text {...props}>{formatMoney(absMoney(amount))}</Text>
      <Icon className={iconClass} />
    </div>
  );
};
